import { Link, useParams } from "react-router-dom"
import { FormEvent, MouseEvent, useEffect, useMemo, useState } from "react";
import { TradeFair, TradeFairRegistration } from "../index.types";
import { get } from "../../../libraries/api";
import { List, Spinner, Dropdown } from '@abm-international/react-components';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faSync, faTimesCircle, faTrash } from "@fortawesome/free-solid-svg-icons";
import './TradeFairRegistrations.scss';
import axios from "axios";
import { DEFAULT_AXIOS_CONFIG } from "../../../api/index";
import { TradeFairAttendanceBlock, calculateAttendees } from "../TradeFairAttendanceBlock/TradeFairAttendanceBlock";
import { Totals } from "./Totals";
import { t } from '../../../libraries/i18n';

const URL = '/api/v1/trade-fairs';
const regex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;

function reformatDateTimeHelper(dateTimeString: string) {
    if (!regex.test(dateTimeString)) return dateTimeString;
    const [datePart, timePart] = dateTimeString.split(' ');
    const [year, month, day] = datePart.split('-');
    const formattedDatePart = `${day}-${month}-${year}`;
    const formattedDateTimeString = `${formattedDatePart} ${timePart}`;
    return formattedDateTimeString;
}


export const formatDate = (date?: string, endDate?: string, timestamped?: boolean) => {
    if (!date) return '';

    let date_d = new Date(date);

    if (timestamped) {
        const offset = date_d.getTimezoneOffset();
        date_d = new Date(date_d.getTime() - offset * 60 * 1000);
    }

    const _date = date_d.toISOString().replace('T', ' ').substring(0, 16);

    if (endDate) {
        const _endDate = (endDate || '').replace('T', ' ').substring(0, 16).replace(_date.substring(0, 11), '');

        return `${reformatDateTimeHelper(_date)} - ${reformatDateTimeHelper(_endDate)}`;
    }

    return reformatDateTimeHelper(_date);
}

const getUniqueDays = (dateRanges: string[]): string[] => {
    const uniqueDays = new Set<string>();
    dateRanges.forEach(range => {
        const [start] = range.split('/');
        const [date] = start.split(' ');
        uniqueDays.add(date);
    });
    return Array.from(uniqueDays);
}

const basicSort = (col: string) => {
    return (a: any, b: any) => {
        const keys = col.split('.');
        let itemValueA = a;
        let itemValueB = b;

        keys.forEach(key => {
            itemValueA = itemValueA?.[key];
            itemValueB = itemValueB?.[key];
        });
    };
};

const defaultNewRegistration: { [index: string]: number | '' } = {
    attendeesAdult: 0,
    attendeesChild1: 0,
    attendeesChild2: 0,
    attendeesChild3: 0,
};

export interface TradeFairRegistrationCount {
    date?: string | Date,
    attendeesAdult: number,
    surPlusAttendeesAdult?: number,
    attendeesChild1: number,
    surPlusAttendeesChild1?: number,
    attendeesChild2: number,
    surPlusAttendeesChild2?: number,
    attendeesChild3: number,
    surPlusAttendeesChild3?: number,
    clients: number,
}

function sumAttendees(tradeFairRegistration: TradeFairRegistration, initial?: TradeFairRegistrationCount, date?: string): TradeFairRegistrationCount {
    if (!tradeFairRegistration.registrations?.length) {
        return initial || {
            attendeesAdult: 0,
            surPlusAttendeesAdult: 0,
            attendeesChild1: 0,
            surPlusAttendeesChild1: 0,
            attendeesChild2: 0,
            surPlusAttendeesChild2: 0,
            attendeesChild3: 0,
            surPlusAttendeesChild3: 0,
            clients: 0,
        };
    }

    const regDay = tradeFairRegistration.attendanceDateStart ? new Date(tradeFairRegistration.attendanceDateStart).toISOString().substring(0, 10) : undefined;
    const totalAttendeesAdult = tradeFairRegistration.registrations.reduce((sum, registration) => {
        return sum + (!date || regDay === date ? registration.attendeesAdult : 0);
    }, 0);
    const maxAttendeesAdult = tradeFairRegistration.attendeesAdult;
    const retAttendeesAdult = totalAttendeesAdult > maxAttendeesAdult ? maxAttendeesAdult : totalAttendeesAdult;
    const retSurPlusAttendeesAdult = totalAttendeesAdult > maxAttendeesAdult ? totalAttendeesAdult - maxAttendeesAdult : 0;
    const retClients = retAttendeesAdult ? 1 : 0

    const totalAttendeesChild1 = tradeFairRegistration.registrations.reduce((sum, registration) => {
        return sum + (registration?.attendeesChild1 && (!date || regDay === date) ? registration?.attendeesChild1 : 0);
    }, 0);
    const maxAttendeesChild1 = tradeFairRegistration.attendeesChild1 || 0;
    const retAttendeesChild1 = totalAttendeesChild1 > maxAttendeesChild1 ? maxAttendeesChild1 : totalAttendeesChild1;
    const retSurPlusAttendeesChild1 = totalAttendeesChild1 > maxAttendeesChild1 ? totalAttendeesChild1 - maxAttendeesChild1 : 0;

    const totalAttendeesChild2 = tradeFairRegistration.registrations.reduce((sum, registration) => {
        return sum + (registration?.attendeesChild2 && (!date || regDay === date) ? registration?.attendeesChild2 : 0);
    }, 0);
    const maxAttendeesChild2 = tradeFairRegistration.attendeesChild2 || 0;
    const retAttendeesChild2 = totalAttendeesChild2 > maxAttendeesChild2 ? maxAttendeesChild2 : totalAttendeesChild2;
    const retSurPlusAttendeesChild2 = totalAttendeesChild2 > maxAttendeesChild2 ? totalAttendeesChild2 - maxAttendeesChild2 : 0;

    const totalAttendeesChild3 = tradeFairRegistration.registrations.reduce((sum, registration) => {
        return sum + (registration?.attendeesChild3 && (!date || regDay === date) ? registration?.attendeesChild3 : 0);
    }, 0);
    const maxAttendeesChild3 = tradeFairRegistration.attendeesChild3 || 0;
    const retAttendeesChild3 = totalAttendeesChild3 > maxAttendeesChild3 ? maxAttendeesChild3 : totalAttendeesChild3;
    const retSurPlusAttendeesChild3 = totalAttendeesChild3 > maxAttendeesChild3 ? totalAttendeesChild3 - maxAttendeesChild3 : 0;

    return {
        attendeesAdult: (initial?.attendeesAdult || 0) + retAttendeesAdult,
        surPlusAttendeesAdult: (initial?.surPlusAttendeesAdult || 0) + retSurPlusAttendeesAdult,
        attendeesChild1: (initial?.attendeesChild1 || 0) + retAttendeesChild1,
        surPlusAttendeesChild1: (initial?.surPlusAttendeesChild1 || 0) + retSurPlusAttendeesChild1,
        attendeesChild2: (initial?.attendeesChild2 || 0) + retAttendeesChild2,
        surPlusAttendeesChild2: (initial?.surPlusAttendeesChild2 || 0) + retSurPlusAttendeesChild2,
        attendeesChild3: (initial?.attendeesChild3 || 0) + retAttendeesChild3,
        surPlusAttendeesChild3: (initial?.surPlusAttendeesChild3 || 0) + retSurPlusAttendeesChild3,
        clients: (initial?.clients || 0) + retClients
    };
}

const test = (registrations?: TradeFairRegistration[], date?: string) => {
    return (registrations || []).reduce<TradeFairRegistrationCount>((sum, registration) => sumAttendees(registration, sum, date), {
        attendeesAdult: 0,
        surPlusAttendeesAdult: 0,
        attendeesChild1: 0,
        surPlusAttendeesChild1: 0,
        attendeesChild2: 0,
        surPlusAttendeesChild2: 0,
        attendeesChild3: 0,
        surPlusAttendeesChild3: 0,
        clients: 0,
    });
}

export const TradeFairRegistrations = () => {
    const { tradeFairId } = useParams<{ tradeFairId?: string, stest?: string }>();

    const [page, setPage] = useState(0);
    const [filter, setFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('all');
    const [tradeFair, setTradeFair] = useState<TradeFair>();
    const [registrations, setRegistrations] = useState<(TradeFairRegistration & { klt_pst?: number, klt_gem?: string })[]>();

    const [fetchingRegistrations, setFetchingRegistrations] = useState(false);
    const [activeRegistration, setActiveRegistration] = useState<TradeFairRegistration>();

    const [newRegistration, setNewRegistration] = useState(defaultNewRegistration);
    const [registering, setRegistering] = useState(false);
    const [registeringError, setRegisteringError] = useState('');

    const [forceFilter, setForceFilter] = useState(Date.now());

    const filteredRegistrations = useMemo(() => {
        if (!filter && !dateFilter) return registrations;
        
        let _registrations = [...(registrations || [])];

        if (dateFilter && dateFilter !== 'all') {
            _registrations = _registrations.filter(registration => {
                const date = new Date(registration.attendanceDateStart).toISOString().substring(0, 10);
                return dateFilter === date;
            })
        }

        if (filter) {
            const _filter = filter.toLowerCase().trim();

            _registrations = _registrations?.filter(registration => (registration.customerName || '')?.toLowerCase().indexOf(_filter) > -1 || String(registration.customerId).toLowerCase().indexOf(_filter) > -1)

            if (_filter && !isNaN(Number(_filter))) {
                _registrations = _registrations?.sort((a, b) => a.customerId > b.customerId ? 1 : -1);
            }
        }

        setPage(0);
        return _registrations;
    }, [registrations, filter, dateFilter, forceFilter])

    const registrationTotalsOnDays = useMemo(() => {
        return getUniqueDays(tradeFair?.dates || []).map(day => {
            return {
                date: day,
                totals: registrations?.length ? test(registrations, day) : {
                    attendeesAdult: 0,
                    surPlusAttendeesAdult: 0,
                    attendeesChild1: 0,
                    surPlusAttendeesChild1: 0,
                    attendeesChild2: 0,
                    surPlusAttendeesChild2: 0,
                    attendeesChild3: 0,
                    surPlusAttendeesChild3: 0,
                    clients: 0
                },
                totalsExpected: (registrations || []).reduce<TradeFairRegistrationCount>((sum, registration) => ({
                    attendeesAdult: sum.attendeesAdult + (registration.attendanceDateStart.indexOf(day) === 0 ? registration.attendeesAdult : 0),
                    attendeesChild1: sum.attendeesChild1 + (registration.attendanceDateStart.indexOf(day) === 0 ? (registration.attendeesChild1 || 0) : 0),
                    attendeesChild2: sum.attendeesChild2 + (registration.attendanceDateStart.indexOf(day) === 0 ? (registration.attendeesChild2 || 0) : 0),
                    attendeesChild3: sum.attendeesChild3 + (registration.attendanceDateStart.indexOf(day) === 0 ? (registration.attendeesChild3 || 0) : 0),
                    clients: sum.clients + (registration.attendanceDateStart.indexOf(day) === 0 ? 1 : 0),
                }), {
                    attendeesAdult: 0,
                    attendeesChild1: 0,
                    attendeesChild2: 0,
                    attendeesChild3: 0,
                    clients: 0
                }),
            }
        });
    }, [registrations, tradeFair?.dates, forceFilter]);

    const [trigger, setTrigger] = useState(Date.now());

    const getProspects = async () => {
        const response = await get(`/api/v1/sync/tradefair/additional`);
        setTrigger(Date.now())
    }

    const updateRegistrations = (oldReg?: any[], newReg?: any[]) => {
        if (!oldReg?.length) {
            return newReg?.length ? newReg : [];
        };

        newReg?.forEach(reg => {
            const index = oldReg?.findIndex(old => old.id === reg.id);

            if (index >= 0) {
                oldReg[index] = reg;
            } else {
                oldReg.push(reg);
            }
        });

        return oldReg;
    }

    useEffect(() => {
        setTradeFair(undefined);
        setFetchingRegistrations(true);

        (async () => {
            const response = await get(`${URL}/${tradeFairId}`);
            if (response.success) {
                setTradeFair(response.data);
                const today = new Date().toISOString().substring(0, 10);
                const index = getUniqueDays((response.data as TradeFair)?.dates || []).findIndex(reg => reg === today);
                if (index > -1) setDateFilter(today);
            }
        })();

        (async () => {
            // TODO: Implement caching if soft-delete is enabled
            // const suffix = registrations?.length && trigger ? '?from=' + (trigger + 1000 * 60 * 60 * 1) : ''; // add timzeone (2 hours) to trigger to fix timeshift with db
            // const response = await get(`${URL}/${tradeFairId}/registrations${suffix}`);

            const response = await get(`${URL}/${tradeFairId}/registrations`);

            if (response.success) {
                // TODO: Implement caching if soft-delete is enabled
                // setRegistrations((prev) => updateRegistrations(prev, response.data));
                // setForceFilter(Date.now());

                setRegistrations(response.data);
            } else {
                setRegistrations([]);
            }
            setFetchingRegistrations(false);
        })();
    }, [tradeFairId, trigger]);

    useEffect(() => {
        setNewRegistration(defaultNewRegistration);
    }, [activeRegistration])

    const resetNewRegistration = () => {
        setNewRegistration(defaultNewRegistration);
        setRegisteringError('');
    };

    const updateActiveRegistration = (e?: MouseEvent<HTMLDivElement>, registration?: TradeFairRegistration) => {
        if (String(e?.target) === '[object HTMLDivElement]' || String(e?.target) === '[object HTMLBodyElement]') {
            e?.stopPropagation();
        }

        if (!registration || activeRegistration?.id === registration?.id) return;
        setActiveRegistration(registration);
    };

    const confirmRegistration = async (e: FormEvent) => {
        e.preventDefault();

        if (!activeRegistration?.id || (!newRegistration)) return;

        setRegistering(true);

        const registration = {
            timestamp: new Date(),
            attendeesAdult: newRegistration.attendeesAdult,
            attendeesChild1: newRegistration.attendeesChild1,
            attendeesChild2: newRegistration.attendeesChild2,
            attendeesChild3: newRegistration.attendeesChild3,
        }

        try {
            const response = await axios.post(`v1/trade-fairs/${tradeFairId}/registration/${activeRegistration.id}/new`, registration, DEFAULT_AXIOS_CONFIG);
            if (response.data.success) {
                resetNewRegistration();
                setRegistrations(prev => {
                    const newRegs = [...(prev || [])];
                    const index = newRegs?.findIndex(reg => reg.id === response.data.data.id);
                    if (index > -1) {
                        newRegs[index] = { ...newRegs[index], ...response.data.data };
                    }
                    return newRegs;
                });
                if (activeRegistration?.id === response.data.data.id) {
                    setActiveRegistration(prev => ({ ...prev, ...response.data.data }));
                }

                // setTrigger(Date.now());
            } else {
                setRegisteringError('There was no success registering the attendance');
            }
            setRegistering(false);
        } catch (err: any) {
            console.error(err);
            setRegisteringError(`${err.body?.error_code || '0'}:  ${err.body?.error_message || 'OEPS'}`);
            // TODO: show notification or something...
            setRegistering(false);
        }
    }

    const removeRegistrationAttendance = async (registration: TradeFairRegistration, index: number) => {
        const answer = window.confirm('Opgelet!\nBent u zeker dat je deze registratie wilt verwijderen?\n(deze actie kan niet ongedaan worden)');
        if (!answer) return;

        try {
            const regToDelete = registration.registrations?.[index];
            const response = await axios.post(`v1/trade-fairs/${tradeFairId}/registration/${registration.id}/deleteRegistrationAttendance`, regToDelete, DEFAULT_AXIOS_CONFIG);

            if (response.data.success) {
                setTrigger(Date.now());

                if (activeRegistration?.id === response.data.data.id) {
                    setActiveRegistration(prev => ({ ...prev, ...response.data.data }));
                }
            }
        } catch (error) {
            console.error(error);
        }
    }

    return (
        <div className="TradeFairRegistrations">
            <div className="tfr-header">
                <Link to='/trade-fairs/' className="return-link">
                    <FontAwesomeIcon icon={faArrowLeft} />
                </Link>

                <h1>Evenementen &gt; {tradeFairId}</h1>

                <div className="refresh-data">
                    Refresh
                    <button
                        className='refresh-btn'
                        onClick={async () => {
                            getProspects();
                        }}
                    ><FontAwesomeIcon icon={faSync} /></button>
                </div>
            </div>

            <div className="totals_container">
                    {registrationTotalsOnDays.map((day, index) => (
                        <Totals key={'totals-' + index} totals={day.totals} totalsExpected={day.totalsExpected} day={day.date} />
                    ))}
            </div>

            <div className="tfr-filter">
                <input
                    type="search"
                    id="filter"
                    name="filter"
                    placeholder="Zoek op unieke code of naam"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                />

                <Dropdown
                    label='Selecteer dag'
                    items={[
                        { value: 'all', display: 'Alle dagen' },
                        ...getUniqueDays(tradeFair?.dates || []).map(d => ({
                            value: d,
                            display: d
                        }))
                    ]}
                    onSelect={(e: string) => setDateFilter(e)}
                    selected={dateFilter}
                />
            </div>

            <div className='visits-wrapper'>
                <List
                    disableKeyboardControls
                    className='visits-list'
                    action={updateActiveRegistration}
                    header={[
                        {
                            key: 0,
                            title: 'Klant',
                            render: ({ customerId, customerName }: { customerId: number, customerName: string }) => <>{customerName} ({customerId})</>,
                            // sort: basicSort('customerId'),
                        },
                        {
                            key: 1,
                            title: 'Adres',
                            render: ({ klt_pst, klt_gem }: { klt_pst: number, klt_gem: string }) => <>{klt_gem}{klt_pst ? ` (${klt_pst})` : ''}</>,
                            // sort: basicSort('customerId'),
                        },
                        {
                            key: 2,
                            title: 'Datum & Tijd',
                            render: ({ attendanceDateStart, attendanceDateEnd }: { attendanceDateStart: string, attendanceDateEnd: string }) => formatDate(attendanceDateStart, attendanceDateEnd),
                            // sort: basicSort('attendanceDateStart'),
                        },
                        {
                            key: 3,
                            title: t('events.label_quantity'),
                            render: ({ attendeesAdult, registrations }: { attendeesAdult: number, registrations: any[] }) => <>{calculateAttendees('attendeesAdult', registrations)} / {attendeesAdult}</>,
                        },
                        ...(tradeFair?.attendeesChild1 ? [{
                            key: 4,
                            title: t('events.label_quantity_1'),
                            render: ({ attendeesChild1, registrations }: { attendeesChild1: number, registrations: any[] }) => <>{calculateAttendees('attendeesChild1', registrations)} / {attendeesChild1}</>,
                        }] : []),
                        ...(tradeFair?.attendeesChild2 ? [{
                            key: 5,
                            title: t('events.label_quantity_2'),
                            render: ({ attendeesChild2, registrations }: { attendeesChild2: number, registrations: any[] }) => <>{calculateAttendees('attendeesChild2', registrations)} / {attendeesChild2}</>,
                        }] : []),
                        ...(tradeFair?.attendeesChild3 ? [{
                            key: 6,
                            title: t('events.label_quantity_3'),
                            render: ({ attendeesChild3, registrations }: { attendeesChild3: number, registrations: any[] }) => <>{calculateAttendees('attendeesChild3', registrations)} / {attendeesChild3}</>,
                        }] : []),
                        {
                            key: 7,
                            title: 'Aanwezigen',
                            render: ({ attendeesAdult, attendeesChild1, attendeesChild2, attendeesChild3, registrations }: { attendeesAdult: number, attendeesChild1: number, attendeesChild2: number, attendeesChild3: number, registrations: any[] }) => <>{!registrations?.length ? 0 : registrations?.reduce((acc, reg) => acc + (reg.attendeesAdult || 0) + (reg.attendeesChild1 || 0) + (reg.attendeesChild2 || 0) + (reg.attendeesChild3 || 0), 0)} / {attendeesAdult + attendeesChild1 + attendeesChild2 + attendeesChild3}</>,
                        },
                    ]}
                    content={fetchingRegistrations ? undefined : filteredRegistrations}
                    pagination={{
                        itemsPerPage: 10,
                        page,
                        setPage
                    }}
                />

                {fetchingRegistrations && <Spinner />}

                {(!fetchingRegistrations && !registrations?.length) && <p>No registrations (yet)</p>}
            </div>

            <div className={'tfr-snackbar' + (activeRegistration?.id ? ' active' : ' inactive')}>
                <div className='tfr-snackbar-content'>
                    <button onClick={() => setActiveRegistration(undefined)} className="tfr-snackbar-close"><FontAwesomeIcon icon={faTimesCircle} /></button>

                    <h3>{activeRegistration?.customerName} ({activeRegistration?.customerId})</h3>

                    <div className="tfr-registration-info">
                        <div className="tfr-info-block">
                            <div className="tfr-info-label">Datum & Tijd:</div>
                            <div className="tfr-info-value">{formatDate(activeRegistration?.attendanceDateStart, activeRegistration?.attendanceDateEnd)}</div>
                        </div>

                        {activeRegistration?.comments && <div className="tfr-info-block">
                            <div className="tfr-info-label">Opmerkingen:</div>
                            <div className="tfr-info-value">{activeRegistration.comments}</div>
                        </div>}

                        <div className="tfr-info-block">
                            <div className="tfr-info-label">Aanwezigen:</div>
                            <TradeFairAttendanceBlock
                                tradeFair={tradeFair}
                                registration={activeRegistration}
                            />
                        </div>
                    </div>

                    <h4>Registreer</h4>

                    <div className="tfr-registration">
                        <form onSubmit={confirmRegistration}>
                            <div>
                                <label htmlFor="newreg-att">{t('events.label_quantity')}</label><br />
                                <input
                                    type="number"
                                    id='newreg-att'
                                    disabled={registering}
                                    min={0}
                                    value={newRegistration.attendeesAdult}
                                    onChange={(e) => setNewRegistration(prev => ({ ...prev, attendeesAdult: !e.target.value ? '' : Number(e.target.value) < 0 ? 0 : Number(e.target.value) }))}
                                    onBlur={() => setNewRegistration(prev => ({ ...prev, attendeesAdult: newRegistration.attendeesAdult || 0 }))}
                                />
                            </div>

                            {tradeFair?.attendeesChild1 && <div>
                                <label htmlFor="newreg-att1">{t('events.label_quantity_1')}</label><br />
                                <input
                                    type="number"
                                    id='newreg-att1'
                                    disabled={registering}
                                    min={0}
                                    value={newRegistration.attendeesChild1}
                                    onChange={(e) => setNewRegistration(prev => ({ ...prev, attendeesChild1: !e.target.value ? '' : Number(e.target.value) < 0 ? 0 : Number(e.target.value) }))}
                                    onBlur={() => setNewRegistration(prev => ({ ...prev, attendeesChild1: newRegistration.attendeesChild1 || 0 }))}
                                />
                            </div>}

                            {tradeFair?.attendeesChild2 && <div>
                                <label htmlFor="newreg-att2">{t('events.label_quantity_2')}</label><br />
                                <input
                                    type="number"
                                    id='newreg-att2'
                                    disabled={registering}
                                    min={0}
                                    value={newRegistration.attendeesChild2}
                                    onChange={(e) => setNewRegistration(prev => ({ ...prev, attendeesChild2: !e.target.value ? '' : Number(e.target.value) < 0 ? 0 : Number(e.target.value) }))}
                                    onBlur={() => setNewRegistration(prev => ({ ...prev, attendeesChild2: newRegistration.attendeesChild2 || 0 }))}
                                />
                            </div>}

                            {tradeFair?.attendeesChild3 && <div>
                                <label htmlFor="newreg-att3">{t('events.label_quantity_3')}</label><br />
                                <input
                                    type="number"
                                    id='newreg-att3'
                                    disabled={registering}
                                    min={0}
                                    value={newRegistration.attendeesChild3}
                                    onChange={(e) => setNewRegistration(prev => ({ ...prev, attendeesChild3: !e.target.value ? '' : Number(e.target.value) < 0 ? 0 : Number(e.target.value) }))}
                                    onBlur={() => setNewRegistration(prev => ({ ...prev, attendeesChild3: newRegistration.attendeesChild3 || 0 }))}
                                />
                            </div>}

                            <div className="form-actions">
                                {registeringError && <span className="tfr-registration-error">{registeringError}</span>}
                                <button type="submit" disabled={registering || (!newRegistration.attendeesAdult && !newRegistration.attendeesChild1 && !newRegistration.attendeesChild2 && !newRegistration.attendeesChild3)}>{registering ? <Spinner size='small' /> : 'Registreer'}</button>

                            </div>
                        </form>
                    </div>

                    <h4>Registratie Overzicht</h4>

                    <div className="tfr-snackbar-registrations">
                        {!activeRegistration?.registrations?.length && <>Nog geen registraties.</>}

                        {activeRegistration?.registrations?.map((reg, index) => (
                            <div key={reg.timestamp + '-' + index} className="tfr-snackbar-reg">
                                <div className="tfr-snackbar-reg-content">
                                    <span className="tfr-snackbar-reg-ts">{formatDate(reg.timestamp, undefined, true)}</span>

                                    <TradeFairAttendanceBlock
                                        tradeFair={tradeFair}
                                        registration={activeRegistration}
                                        attendance={reg}
                                        hideMax
                                    />
                                </div>

                                <div className="tfr-snackbar-reg-actions">
                                    <button className="tfr-snackbar-reg-remove" onClick={() => removeRegistrationAttendance(activeRegistration, index)}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
